import { ChangeEvent, FC } from 'react'

import { FieldMetadata, useInputControl } from '@conform-to/react'
import { z } from 'zod'

import { TextInput } from '~/components'
import { SearchFormSectionLayout } from '~/features/common/search-form-sections/section-layout'

export const monthlyCostsFormSchema = z.object({
  minMonthlyRent: z.coerce.number().int().min(1).optional(),
  maxMonthlyRent: z.coerce.number().int().min(1).optional(),
})

type MonthlyCostsFormSchemaType = z.infer<typeof monthlyCostsFormSchema>

export const partialMonthlyCostsFormSchema = monthlyCostsFormSchema.partial()

export const useMonthlyCostsForm = (
  minMeta: FieldMetadata<MonthlyCostsFormSchemaType['minMonthlyRent']>,
  maxMeta: FieldMetadata<MonthlyCostsFormSchemaType['maxMonthlyRent']>,
  changeWatchedMinMonthlyRent?: (minMonthlyRent: string) => void,
  changeWatchedMaxMonthlyRent?: (maxMonthlyRent: string) => void,
) => {
  const minControl = useInputControl({
    ...minMeta,
    initialValue: minMeta.initialValue ?? '',
  })
  const maxControl = useInputControl({
    ...maxMeta,
    initialValue: maxMeta.initialValue ?? '',
  })

  const onChangeMinMonthlyRent = (e: React.ChangeEvent<HTMLInputElement>) => {
    minControl.change(e.target.value)
  }
  const onChangeMaxMonthlyRent = (e: React.ChangeEvent<HTMLInputElement>) => {
    maxControl.change(e.target.value)
  }

  const onBlurMinMonthlyRent = () => {
    changeWatchedMinMonthlyRent?.(minControl.value || '')
  }

  const onBlurMaxMonthlyRent = () => {
    changeWatchedMaxMonthlyRent?.(maxControl.value || '')
  }

  return {
    minMonthlyRentValue: minControl.value || '',
    maxMonthlyRentValue: maxControl.value || '',
    onChangeMinMonthlyRent,
    onChangeMaxMonthlyRent,
    minMonthlyRentName: minMeta.name,
    maxMonthlyRentName: maxMeta.name,
    onBlurMinMonthlyRent,
    onBlurMaxMonthlyRent,
  }
}

type MonthlyCostsSectionProps = {
  minValueErrorMessages?: string[]
  maxValueErrorMessages?: string[]
  headerClassName?: string
  layoutClassName?: string
  minMonthlyRentValue: string
  maxMonthlyRentValue: string
  onChangeMinMonthlyRent: (e: ChangeEvent<HTMLInputElement>) => void
  onChangeMaxMonthlyRent: (e: ChangeEvent<HTMLInputElement>) => void
  minMonthlyRentName: string
  maxMonthlyRentName: string
  onBlurMinMonthlyRent?: () => void
  onBlurMaxMonthlyRent?: () => void
}

export const MonthlyCostsSection: FC<MonthlyCostsSectionProps> = ({
  minMonthlyRentValue,
  maxMonthlyRentValue,
  onChangeMinMonthlyRent,
  onChangeMaxMonthlyRent,
  minMonthlyRentName,
  maxMonthlyRentName,
  minValueErrorMessages,
  maxValueErrorMessages,
  layoutClassName,
  headerClassName,
  onBlurMinMonthlyRent,
  onBlurMaxMonthlyRent,
}) => {
  return (
    <SearchFormSectionLayout
      headerClassName={headerClassName}
      layoutClassName={layoutClassName}
      title="月額費用総額"
    >
      <div className="grow p-4">
        <div className="mb-2.5 flex items-start justify-between gap-x-2">
          <div className="min-w-0 grow">
            <TextInput
              errorMessages={minValueErrorMessages}
              name={minMonthlyRentName}
              onBlur={onBlurMinMonthlyRent}
              onChange={onChangeMinMonthlyRent}
              placeholder="下限なし"
              value={minMonthlyRentValue}
            />
          </div>
          <div className="flex h-[52px] items-center">
            <span className="text-body">〜</span>
          </div>
          <div className="min-w-0 grow">
            <TextInput
              errorMessages={maxValueErrorMessages}
              name={maxMonthlyRentName}
              onBlur={onBlurMaxMonthlyRent}
              onChange={onChangeMaxMonthlyRent}
              placeholder="上限なし"
              value={maxMonthlyRentValue}
            />
          </div>
        </div>
      </div>
    </SearchFormSectionLayout>
  )
}
